import { Component, EventEmitter, inject, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { firstValueFrom, Subscription } from 'rxjs';
import { ProjectVersion } from 'src/app/constants/project-version-constans';
import { FormatDetailModel } from 'src/app/models/formats/format-detail.model';
import { FormatModel } from 'src/app/models/formats/format.model';
import { MaintenanceOrderPrintModel } from 'src/app/models/maintenance/maintenance-order-print.model';
import { FormatDetailService } from 'src/app/services/formats/format-detail.service';
import { PrintPageSizeService } from 'src/app/services/global-services/print-page-size.service';
import { UserAuthenticatedService } from 'src/app/services/global-services/user-authenticated.service';
import { EnterpriseService } from 'src/app/services/management/enterprise.service';

@Component({
  selector: 'app-print-maintenance-order-scheduled',
  templateUrl: './print-maintenance-order-scheduled.component.html',
  styleUrls: ['./print-maintenance-order-scheduled.component.scss'],
  standalone: false
})
export class PrintMaintenanceOrderScheduledComponent implements OnInit {
  private formatDetailService = inject(FormatDetailService);
  private userAuthenticatedService = inject(UserAuthenticatedService);
  private printPageSizeService = inject(PrintPageSizeService);
  private enterpriseService = inject(EnterpriseService);
  private domSanitizer = inject(DomSanitizer);

  @Input() companyId: number | null = 0;
  @Input() editFormat: boolean = false;
  @Input() exportFormat: boolean = false;
  @Input() focusText: boolean = false;
  @Input() formatDetail: FormatDetailModel = new FormatDetailModel();
  @Input() formatModel: FormatModel = new FormatModel();
  @Input() maintenanceOrderPrintContract: MaintenanceOrderPrintModel = new MaintenanceOrderPrintModel();
  @Input() onBlank: boolean = false;
  @Input() reloadPrint: boolean = false;
  @Output() emitFormatDetail = new EventEmitter<FormatDetailModel>();
  @Output() finishLoadingPrint = new EventEmitter<boolean>();

  bottomPreview: number = 0;
  columnsActivities: { field: string; header: string; }[] = [];
  idMapActivities: { [key: string]: { id: string; type: string; showTotal: boolean; } } = {
    'maintenanceActivitiesCode': { id: 'codigo-actividad', type: 'text', showTotal: false },
    'maintenanceActivitiesDescription': { id: 'descripcion-actividad', type: 'text', showTotal: false },
    'executed': { id: 'ejecutada', type: 'text', showTotal: false },
    'rescheduled': { id: 'reprogramada', type: 'text', showTotal: false },
    'observation': { id: 'observaciones', type: 'text', showTotal: false },
  };

  columnsSupplies: { field: string; header: string; }[] = [];
  idMapSupplies: { [key: string]: { id: string; type: string; showTotal: boolean; printFormat: string | null; } } = {
    'articleCode': { id: 'codigo-articulo', type: 'text', showTotal: false, printFormat: null },
    'articleName': { id: 'nombre-articulo', type: 'text', showTotal: false, printFormat: null },
    'quantity': { id: 'cantidad', type: 'decimal', showTotal: false, printFormat: null },
  };

  dateNow: Date = new Date();
  element: any;
  elementStyle: any;
  elementsBodyActivities: FormatDetailModel[] = [];
  elementsBodySupplies: FormatDetailModel[] = [];
  elementsLabel: FormatDetailModel[] = [];
  id = '';
  isShift = false;
  leftPreview: number = 0;
  listFormatDetailUpdates: FormatDetailModel[] = [];
  listObservers: Array<Subscription> = new Array<Subscription>();
  logoEnterprise: SafeUrl = "";
  mouseDown: boolean = false;
  newFormatDetail: FormatDetailModel = new FormatDetailModel();
  rightPreview: number = 0;
  shiftX: number = 0;
  shiftY: number = 0;
  speed = 2;
  titleForm: string = '';
  topPreview: number = 0;
  userAuthenticated: string = '';
  version: string = ProjectVersion.projectVersion;

  private keydownListener!: (e: KeyboardEvent) => void;
  private mouseupListener!: () => void;
  private mousemoveListener!: (e: MouseEvent) => void;
  private keyupListener!: (e: KeyboardEvent) => void;

  ngOnInit() {
    this.getUserAutheticated()
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    const maintenanceRoutinePrintContract = changes["maintenanceOrderPrintContract"];
    const exportFormat = changes["exportFormat"];
    const formatDetail = changes["formatDetail"];
    const formatModel = changes["formatModel"];

    if (maintenanceRoutinePrintContract && !maintenanceRoutinePrintContract.firstChange) {
      if (this.maintenanceOrderPrintContract.enterpriseLogo != null) await this.getLogo(this.maintenanceOrderPrintContract.enterpriseLogo);
      await this.configureFormatByDocument();
    }

    if (exportFormat && !exportFormat.firstChange) { this.updateRangeFormatDetail(); }
    if (formatModel && formatModel.firstChange) { this.formatModel = formatModel.currentValue; }
    if (formatDetail && !formatDetail.firstChange) { this.changeFormatDetail(); }
  }

  ngOnDestroy() {
    this.listObservers.forEach(sub => sub.unsubscribe());
    this.printPageSizeService.removePageSizeStyle();

    // Remover event listeners globales
    if (this.keydownListener) document.removeEventListener('keydown', this.keydownListener);
    if (this.mouseupListener) document.removeEventListener('mouseup', this.mouseupListener, true);
    if (this.mousemoveListener) document.removeEventListener('mousemove', this.mousemoveListener, true);
    if (this.keyupListener) document.removeEventListener('keyup', this.keyupListener);
  }

  ngAfterViewInit() {
    if (this.editFormat) {
      this.keydownListener = (e: KeyboardEvent) => {
        const target = e.target as HTMLElement;
        if (
          target &&
          (target.tagName === 'INPUT' ||
            target.tagName === 'TEXTAREA' ||
            target.tagName === 'SELECT' ||
            target.isContentEditable)
        ) {
          return;
        }
        // 2. Si no hay ningún elemento seleccionado para mover, no bloquear
        if (!this.element) {
          return;
        }

        // Deshabilita desplazamiento del scroll con la tecla espacio y flechas
        if (["Space", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].indexOf(e.code) > -1) {
          this.blockScrollArrows(e);
        }

        if (e.which == 16) this.isShift = true;
        //Mover element con flechas
        if (!this.isShift) {
          switch (e.key) {
            case 'ArrowLeft': { this.moveLeft(); break; }
            case 'ArrowRight': { this.moveRight(); break; }
            case 'ArrowUp': { this.moveUp(); break; }
            case 'ArrowDown': { this.moveDown(); break; }
          }
        }

        //Modificar element tamaño de objeto
        if (this.isShift) {
          switch (e.which) {
            case 37: { this.decreaseWidth(); break; }
            case 38: { this.decreaseHeight(); break; }
            case 39: { this.increaseWidth(); break; }
            case 40: { this.increaseHeight(); break; }
          }
        }
      };

      this.mouseupListener = () => {
        this.onMouseUp();
      };

      this.mousemoveListener = (e: MouseEvent) => {
        this.onMouseMove(e);
      };

      this.keyupListener = (e: KeyboardEvent) => {
        if (e.which == 16) this.isShift = false;
      };

      // Registrar los listeners en el document
      document.addEventListener('keydown', this.keydownListener);
      document.addEventListener('mouseup', this.mouseupListener, true);
      document.addEventListener('mousemove', this.mousemoveListener, true);
      document.addEventListener('keyup', this.keyupListener);
    }
  }

  private adjustElementSize(property: 'height' | 'width', delta: number) {
    if (this.focusText) return;

    const currentValue = this.elementStyle.getPropertyValue(property);
    const numericValue = parseFloat(currentValue.replace('px', ''));

    const newValue = numericValue + delta;
    this.element.style[property] = `${newValue}px`;

    this.onClick();
  }

  private async applyColumnStyles(maintenanceOrderPrintContract: MaintenanceOrderPrintModel) {

    // Encabezado de actividades
    for (const column of this.columnsActivities.map(x => x.field)) {
      const element = this.elementsBodyActivities.find(x => x.description === column);
      if (!element) continue;

      this.element = document.getElementById(`${element.name}-${maintenanceOrderPrintContract.maintenanceOrderId}`);
      if (this.element) {
        this.element.style.fontSize = `${element.fontSize}px`;
        this.element.style.width = `${element.width}%`;
        await this.changeAlignment(element.alignment);
      }
    }

    // Encabezado de suministros
    for (const column of this.columnsSupplies.map(x => x.field)) {
      const element = this.elementsBodySupplies.find(x => x.description === column);
      if (!element) continue;
      this.element = document.getElementById(`${element.name}-${maintenanceOrderPrintContract.maintenanceOrderId}`);
      if (this.element) {
        this.element.style.fontSize = `${element.fontSize}px`;
        this.element.style.width = `${element.width}%`;
        await this.changeAlignment(element.alignment);
      }
    }
  }

  private applyElementStyles(element: FormatDetailModel) {
    if (!element.visible) {
      this.element!.setAttribute('hidden', '');
      return;
    }
    const style = this.element!.style;
    style.fontSize = `${element.fontSize}px`;
    this.changeAlignment(element.alignment);
    this.changeBold(element.bold);
    this.changeBorder(element.border);

    if (element.name.includes('foot-')) {
      style.height = `${element.height}px`;
      style.width = `${element.width}px`;
    } else {
      style.backgroundColor = 'transparent';
      style.width = `${element.width}px`;
      if ((!element.name.includes('foot-group') && !element.name.includes('titulo-'))) {
        style.position = 'absolute';
        style.left = `${element.left}%`;
        style.top = `${element.top}%`;
        style.height = `${element.height}px`;
      }
    }
  }

  private async applyFormatDetails(elements: FormatDetailModel[], maintenanceOrderPrintContract: MaintenanceOrderPrintModel) {
    for (const element of elements) {
      if (element.band === 'body-act' || element.band === 'body-sup') continue;
      if (element.name.includes('foot-group')) {
        setTimeout(() => { this.groupingClasses(element); }, 200);
      }
      else {
        setTimeout(async () => {
          this.element = document.getElementById(`${element.name}-${maintenanceOrderPrintContract.maintenanceOrderId}`);
          if (this.element) {
            this.applyElementStyles(element);
          }
        }, 200);
      }
    }
  }

  private applyFormatStyles(element: HTMLElement) {
    element.style.fontSize = `${this.formatDetail.fontSize}px`;
    this.changeAlignment(this.formatDetail.alignment);
    this.changeBold(this.formatDetail.bold);
    this.changeBorder(this.formatDetail.border);

    //No permito mover elementos de los titulos
    if (!this.formatDetail.name.includes('foot-group') && !this.formatDetail.name.includes('titulo-')) {
      this.element.style.position = 'absolute';
      this.element.style.left = `${this.formatDetail.left}%`;
      this.element.style.top = `${this.formatDetail.top}%`;
    }

    //No permito cambiar altura de elementos de la observacion de sucursal
    if (!this.formatDetail.name.includes('foot-group')) {
      element.style.height = `${this.formatDetail.height}px`;
    }
    element.style.width = `${this.formatDetail.width}px`;
  }

  private applyHeaderFooterHeights(maintenanceRoutineId: number, formatModel: FormatModel) {
    setTimeout(async () => {
      const head = document.getElementById(`head-${maintenanceRoutineId}`);
      const foot = document.getElementById(`foot-${maintenanceRoutineId}`);
      head!.style.height = `${formatModel.head}px`;
      foot!.style.height = `${formatModel.foot}px`;

    }, 200);
  }

  async applyParagraphStyles(maintenanceOrderPrint: MaintenanceOrderPrintModel) {

    this.elementsBodyActivities
      .filter(x => x.type === 'p')
      .forEach(element => {
        maintenanceOrderPrint.maintenanceOrderActivities!.forEach(detail => {

          const id = element.name.includes("footer") ? detail.maintenanceOrderId : detail.maintenanceOrderActivitiesId;
          this.element = document.getElementById(`${element.name}-${id}`);
          if (this.element) {
            this.element.style.fontSize = `${element.fontSize}px`;
            this.changeAlignment(element.alignment);
            this.changeBold(element.bold);
          }
        });
      });
    this.elementsBodySupplies
      .filter(x => x.type === 'p')
      .forEach(element => {
        maintenanceOrderPrint.maintenanceOrderSupplies!.forEach(detail => {
          const id = detail.maintenanceOrderSuppliesId;
          this.element = document.getElementById(`${element.name}-${id}`);
          if (this.element) {
            this.element.style.fontSize = `${element.fontSize}px`;
            this.changeAlignment(element.alignment);
            this.changeBold(element.bold);
          }
        });
      });
  }

  blockScrollArrows(e: any) {
    if (!this.focusText) {
      e.preventDefault();
    }
  }

  private buildDisplayedColumns() {
    this.columnsActivities = [];
    this.elementsBodyActivities.filter(x => x.type === 'label').forEach(element => {
      this.columnsActivities.push({ field: element.description!, header: element.text! });
    });

    this.columnsSupplies = [];
    this.elementsBodySupplies.filter(x => x.type === 'label' && x.name !== 'titulo-insumos').forEach(element => {
      this.columnsSupplies.push({ field: element.description!, header: element.text! });
      this.idMapSupplies[element.description!].printFormat = element.printFormat ?? null;
    });
  }

  changeAlignment(alignment: string) {
    this.element.style.textAlign = alignment;
  }

  changeBold(bold: string) {
    this.element.style.fontWeight = bold;
  }

  changeBorder(border: string) {
    switch (border) {
      case 'noborder': {
        this.element.style.border = '0px none rgb(0, 0, 0)';
        break;
      }
      case 'border': {
        this.element.style.border = '1px solid rgb(0, 0, 0)';
        break;
      }
      case 'underline': {
        this.element.style.border = '0px none rgb(0, 0, 0)';
        this.element.style.borderBottom = '1px solid rgb(0, 0, 0)';
        break;
      }
    }
  }

  changeFormatActivities(isLabel: boolean, formatDetailId: number) {
    const element = this.elementsBodyActivities.find(x => x.formatDetailId === formatDetailId);
    if (!element) return;
    element.alignment = this.formatDetail.alignment;
    element.fontSize = this.formatDetail.fontSize;

    if (isLabel) {
      element.visible = this.formatDetail.visible;
      element.width = this.formatDetail.width;
      element.text = this.formatDetail.text;

      if (!this.formatDetail.visible) {
        this.columnsActivities = this.columnsActivities.filter(x => x.field !== element.description);
      } else {
        this.maintenanceOrderPrintContract.maintenanceOrderActivities!.forEach(detail => {
          if (this.element == null) {
            this.element = document.getElementById(`${this.formatDetail.name}-${detail.maintenanceOrderId}`);
          }
          this.updateElementWidth(this.formatDetail.name, detail.maintenanceOrderId!);
          this.updateElementStyle(this.formatDetail.name, detail.maintenanceOrderId!);
          this.changeAlignment(this.formatDetail.alignment);
        });
      }
    } else {
      element.bold = this.formatDetail.bold;
      this.maintenanceOrderPrintContract.maintenanceOrderActivities!.forEach(detail => {
        if (detail.maintenanceOrderActivitiesId) {
          const id = this.formatDetail.name.includes("footer") ? detail.maintenanceOrderId : detail.maintenanceOrderActivitiesId;
          if (this.element == null) {
            this.element = document.getElementById(`${this.formatDetail.name}-${id}`);
          }
          this.updateElementStyle(this.formatDetail.name, id);
          this.changeAlignment(this.formatDetail.alignment);
          this.changeBold(this.formatDetail.bold);
        }
      });
    }
    return element
  }

  changeFormatSupplies(isLabel: boolean, formatDetailId: number) {
    const element = this.elementsBodySupplies.find(x => x.formatDetailId === formatDetailId);
    if (!element) return;
    element.alignment = this.formatDetail.alignment;
    element.fontSize = this.formatDetail.fontSize;

    if (isLabel) {
      element.visible = this.formatDetail.visible;
      element.width = this.formatDetail.width;
      element.text = this.formatDetail.text;
      element.printFormat = this.formatDetail.printFormat;

      if (!this.formatDetail.visible) {
        this.columnsSupplies = this.columnsSupplies.filter(x => x.field !== element.description)
      } else {
        if (this.element == null) {
          this.element = document.getElementById(`${this.formatDetail.name}-${this.maintenanceOrderPrintContract.maintenanceOrderId}`);
        }
        this.updateElementWidth(this.formatDetail.name, this.maintenanceOrderPrintContract.maintenanceOrderId!);
        this.updateElementStyle(this.formatDetail.name, this.maintenanceOrderPrintContract.maintenanceOrderId!);
        this.changeAlignment(this.formatDetail.alignment);
      }
    } else {
      element.bold = this.formatDetail.bold;
      this.maintenanceOrderPrintContract.maintenanceOrderSupplies!.forEach(detail => {
        if (detail.maintenanceOrderSuppliesId) {
          const id = detail.maintenanceOrderSuppliesId;
          if (this.element == null) {
            this.element = document.getElementById(`${this.formatDetail.name}-${id}`);
          }
          this.updateElementStyle(this.formatDetail.name, id);
          this.changeAlignment(this.formatDetail.alignment);
          this.changeBold(this.formatDetail.bold);
        }
      });
    }
    return element
  }

  changeFormatDetail() {
    const formatDetailId = this.formatDetail.formatDetailId;
    const isBodyActivities = this.formatDetail.band === 'body-act';
    const isBodySupplies = this.formatDetail.band === 'body-sup';
    const isLabel = this.formatDetail.type === 'label';

    if (formatDetailId == 0) return;

    if (isBodyActivities) {
      //Actividades
      const element = this.changeFormatActivities(isLabel, formatDetailId);
      if (!element) return;
      this.elementsBodyActivities = this.elementsBodyActivities.filter(x => x.formatDetailId !== formatDetailId);
      this.elementsBodyActivities.push(element);

    } else if (isBodySupplies) {
      //Insumos
      const element = this.changeFormatSupplies(isLabel, formatDetailId);
      if (!element) return;
      this.elementsBodySupplies = this.elementsBodySupplies.filter(x => x.formatDetailId !== formatDetailId);
      this.elementsBodySupplies.push(element);

      const fieldName = element.description;
      if (fieldName && this.idMapSupplies[fieldName]) {
        this.idMapSupplies[fieldName].printFormat = element.printFormat;
      }
    }
    else {
      if (this.formatDetail.name.includes('foot-group')) {
        this.groupingClasses(this.formatDetail);
      } else {

        this.updateElementStyle(this.formatDetail.name, this.maintenanceOrderPrintContract.maintenanceOrderId!);
        if (this.element == null) {
          this.element = document.getElementById(`${this.formatDetail.name}-${this.maintenanceOrderPrintContract.maintenanceOrderId}`);
        }
        if (!this.formatDetail.visible) {
          this.element.setAttribute('hidden', '');
        } else {
          this.applyFormatStyles(this.element);
          this.updateLabelText(formatDetailId, this.formatDetail.text!);
          this.listFormatDetailUpdates = this.listFormatDetailUpdates.filter(x => x.formatDetailId !== formatDetailId);
          this.listFormatDetailUpdates.push(this.formatDetail);
        }

        const labelElement = this.elementsLabel.find(x => x.name === this.formatDetail.name);
        if (labelElement) {
          labelElement.printFormat = this.formatDetail.printFormat;
          this.elementsLabel = [...this.elementsLabel];
        }
      }
    }
  }

  async chargeFormat(formatModel: FormatModel): Promise<boolean> {
    return new Promise((resolve) => {
      (async () => {
        this.formatModel = formatModel;
        this.printPageSizeService.setPageSizeStyle(formatModel.paperSizeId);

        this.applyHeaderFooterHeights(this.maintenanceOrderPrintContract.maintenanceOrderId!, formatModel);
        this.extractFormatDetails(formatModel);

        this.buildDisplayedColumns();
        await this.applyFormatDetails(formatModel.formatDetails, this.maintenanceOrderPrintContract);

        setTimeout(() => {
          this.applyColumnStyles(this.maintenanceOrderPrintContract);
          this.applyParagraphStyles(this.maintenanceOrderPrintContract);
          resolve(true);
        }, 200);
      })();
    });
  }

  clickTable(event: any) {
    if (!this.editFormat) return;

    const element = document.getElementById(event.target.id);
    if (!element) return;

    const elementStyle = window.getComputedStyle(element);
    const name = event.target.id.substring(0, event.target.id.lastIndexOf('-'));
    const formatDetail = this.formatModel.formatDetails.find(x => x.name === name);
    const bodyElementActivities = this.elementsBodyActivities.find(x => x.name === name);
    // const bodyElementStrategies = this.elementsBodyStrategies.find(x => x.name === name);
    const bodyElementSupplies = this.elementsBodySupplies.find(x => x.name === name);

    if (!formatDetail) return;
    this.newFormatDetail = new FormatDetailModel();
    this.newFormatDetail.name = name;
    this.newFormatDetail.formatDetailId = formatDetail.formatDetailId;
    this.newFormatDetail.formatId = this.formatModel.formatId;
    this.newFormatDetail.description = formatDetail.description;
    this.newFormatDetail.band = formatDetail.band;
    this.newFormatDetail.type = formatDetail.type;
    if (bodyElementActivities) {
      this.newFormatDetail.visible = bodyElementActivities.visible;
      this.newFormatDetail.text = bodyElementActivities.text;
      this.newFormatDetail.orderTable = bodyElementActivities.orderTable;
    }

    if (bodyElementSupplies) {
      this.newFormatDetail.visible = bodyElementSupplies.visible;
      this.newFormatDetail.text = bodyElementSupplies.text;
      this.newFormatDetail.orderTable = bodyElementSupplies.orderTable;
      this.newFormatDetail.printFormat = bodyElementSupplies.printFormat;
    }

    // Ancho
    if (formatDetail.type === 'label') {
      const rawWidth = element.style.width;
      const width = parseFloat(rawWidth.replace('%', '')) || 0;
      this.newFormatDetail.width = parseFloat(width.toFixed(4));
    } else {
      this.newFormatDetail.width = 0;
    }

    // Tamaño de fuente
    const fontSize = parseInt(elementStyle.getPropertyValue('font-size').replace('px', ''));
    this.newFormatDetail.fontSize = isNaN(fontSize) ? 0 : fontSize;

    // Negrita
    const fontWeight = elementStyle.getPropertyValue('font-weight');
    this.newFormatDetail.bold = fontWeight === '400' ? 'normal' : 'bold';

    // Alineación
    this.newFormatDetail.alignment = elementStyle.getPropertyValue('text-align');
    this.emitFormatDetail.emit(this.newFormatDetail);
  }


  async configureFormatByDocument() {
    this.chargeFormat(this.formatModel).then(() => {
      this.dateNow = new Date();
      setTimeout(() => {
        this.finishLoadingPrint.emit(true);

      }, 100);
    });
  }

  decreaseHeight() {
    this.adjustElementSize('height', -this.speed);
  }

  increaseHeight() {
    this.adjustElementSize('height', this.speed);
  }

  decreaseWidth() {
    this.adjustElementSize('width', -this.speed);
  }

  increaseWidth() {
    this.adjustElementSize('width', this.speed);
  }

  private extractFormatDetails(formatModel: FormatModel) {
    this.elementsLabel = formatModel.formatDetails.filter(x => x.type === 'label');

    this.elementsBodyActivities = formatModel.formatDetails.filter(x => x.band === 'body-act' && x.visible);
    this.elementsBodySupplies = formatModel.formatDetails.filter(x => x.band === 'body-sup' && x.visible);

    this.elementsBodyActivities.sort((a, b) => a.orderTable - b.orderTable);
    this.elementsBodySupplies.sort((a, b) => a.orderTable - b.orderTable);
  }

  getUserAutheticated() {
    return new Promise((resolve) => {
      this.userAuthenticatedService.userObservable.subscribe(value => {
        this.userAuthenticated = value.userName;
      });
      resolve(true);
    });
  }

  private getStylePx(property: string): number {
    return parseFloat(this.elementStyle.getPropertyValue(property).replace('px', '')) || 0;
  }

  private getStylePxValue(property: string): number {
    const value = this.elementStyle.getPropertyValue(property);
    return parseFloat(value.replace('px', '')) || 0;
  }

  private getBorderStyle(): string {
    //Borde
    if (this.elementStyle.getPropertyValue('border')) {
      if (this.elementStyle.getPropertyValue('border') === '0px none rgb(0, 0, 0)') {
        return 'noborder';
      } else {
        return 'border';
      }
    } else {
      return 'underline';
    }
  }

  getLabelPrintFormat(name: string): string | null {
    return this.elementsLabel.find(x => x.name === name)?.printFormat ?? null;
  }

  async getLogo(enterpriseLogo: string) {
    let blob: Blob | null = null;
    try {
      blob = await firstValueFrom(this.enterpriseService.getEnterpriseLogoAsync(enterpriseLogo));
      if (blob) {
        const objectURL = URL.createObjectURL(blob);
        this.logoEnterprise = this.domSanitizer.bypassSecurityTrustUrl(objectURL);
      }
    } catch (error) {
      console.error('Error al obtener el logo de la empresa.', error);
    }
  }


  async groupingClasses(element: FormatDetailModel) {
    const classMap: { [key: string]: string } = {
      "foot-group-componente": ".group-componente",
      "foot-group-elemento": ".group-elemento"
    };

    const selector = classMap[element.name];
    if (selector) {
      const classEdit = document.querySelectorAll(selector);
      await this.setGroupingStyles(element, classEdit);
    }
  }

  moveDown() {
    this.moveElement('top', this.speed, this.bottomPreview, true);
  }

  private moveElement(direction: 'top' | 'left', delta: number, previewLimit: number, isVertical: boolean) {
    this.focusText || !this.elementStyle
    if (this.focusText || !this.elementStyle) return;

    const currentValue = this.elementStyle.getPropertyValue(direction);
    let position = parseFloat(currentValue.replace('px', ''));

    position += delta;

    const sizeProp = isVertical ? 'height' : 'width';
    const size = parseFloat(this.elementStyle.getPropertyValue(sizeProp).replace('px', ''));

    // Restringir dentro del contenedor
    if (position < 0) position = 0;
    if (position + size > previewLimit) position = previewLimit - size;

    const percentage = ((position / previewLimit) * 100).toFixed(4);
    this.element.style[direction] = `${percentage}%`;

    this.onClick();
  }

  moveLeft() {
    this.moveElement('left', -this.speed, this.rightPreview, false);
  }

  moveRight() {
    this.moveElement('left', this.speed, this.rightPreview, false);
  }

  moveUp() {
    this.moveElement('top', -this.speed, this.bottomPreview, true);
  }

  onColumnActivitiesReorder() {
    this.columnsActivities.forEach((col, index) => {
      this.elementsBodyActivities.find(x => x.description == col.field)!.orderTable = index;
    });
  }

  onColumnSuppliesReorder(columns: { field: string; header: string; }[]) {
    columns.forEach((col, index) => {
      this.elementsBodySupplies.find(x => x.description == col.field)!.orderTable = index;
    });
  }

  onClick() {
    if (!this.editFormat) return;
    const name = this.id.substring(0, this.id.lastIndexOf('-'));
    const formatDetail = this.formatModel.formatDetails.find(x => x.name === name);
    if (!formatDetail) return;

    this.newFormatDetail = new FormatDetailModel();
    this.newFormatDetail.name = name;
    this.newFormatDetail.formatDetailId = formatDetail.formatDetailId;
    this.newFormatDetail.formatId = this.formatModel.formatId;
    this.newFormatDetail.band = formatDetail.band;
    this.newFormatDetail.description = formatDetail.description;
    this.newFormatDetail.text = formatDetail.text;
    this.newFormatDetail.visible = true;
    this.newFormatDetail.printFormat = formatDetail.printFormat;

    this.newFormatDetail.fontSize = this.getStylePxValue('font-size');
    this.newFormatDetail.bold = this.elementStyle.getPropertyValue('font-weight') === '400' ? 'normal' : 'bold';
    this.newFormatDetail.alignment = this.elementStyle.getPropertyValue('text-align');
    this.newFormatDetail.border = this.getBorderStyle();

    //No permito mover elementos totales y de la observacion de sucursal
    if (!name.includes('foot-') && !name.includes('titulo-')) {
      this.newFormatDetail.left = parseFloat(((this.getStylePxValue('left') / this.rightPreview) * 100).toFixed(4));
      this.newFormatDetail.top = parseFloat(((this.getStylePxValue('top') / this.bottomPreview) * 100).toFixed(4));
      this.newFormatDetail.height = parseFloat(this.getStylePxValue('height').toFixed(4));
    }

    this.newFormatDetail.width = parseFloat(this.getStylePxValue('width').toFixed(4));
    this.emitFormatDetail.emit(this.newFormatDetail);
    this.listFormatDetailUpdates = this.listFormatDetailUpdates.filter(x => x.formatDetailId !== this.newFormatDetail.formatDetailId);
    this.listFormatDetailUpdates.push(this.newFormatDetail);
  }

  onDragStart() {
    return false;
  }

  onMouseDown(event: any) {
    if (!this.editFormat) return;

    if (this.element) {
      this.element.style.backgroundColor = 'transparent';
    }

    this.mouseDown = true;
    this.id = event.target.id;
    this.element = document.getElementById(this.id);
    if (!this.element) return;

    this.elementStyle = window.getComputedStyle(this.element);
    this.element.style.backgroundColor = '#0d6efd6e';

    const name = this.id.substring(0, this.id.lastIndexOf('-'));
    const formatDetail = this.formatModel.formatDetails.find(x => x.name === name);

    if (!formatDetail) return;

    const relativeElement = document.getElementById(`${formatDetail.band}-${this.maintenanceOrderPrintContract.maintenanceOrderId}`);

    if (!relativeElement) return;

    const relativeStyle = window.getComputedStyle(relativeElement);

    this.leftPreview = Math.round(relativeElement.offsetLeft - relativeElement.scrollLeft);
    this.topPreview = Math.round(relativeElement.offsetTop - relativeElement.scrollTop);
    this.rightPreview = parseInt(relativeStyle.width.replace('px', ''));
    this.bottomPreview = parseInt(relativeStyle.height.replace('px', ''));

    //No permito mover elementos totales y  observacion de sucursal
    if (!this.id.includes('foot-')) {
      this.element.style.cursor = 'move';

      const leftPX = event.pageX - this.leftPreview;
      const topPX = event.pageY - this.topPreview;

      this.shiftX = leftPX - this.getStylePx('left');
      this.shiftY = topPX - this.getStylePx('top');
    }

    this.onClick();
  }

  onMouseMove(event: any) {
    if (this.mouseDown && !this.id.includes('foot-group') && !this.id.includes('titulo-')) {
      this.element.style.position = 'absolute';
      const leftPx = Math.max(0, event.pageX - this.leftPreview - this.shiftX);
      const widthPx = this.getStylePx('width');
      const finalLeft = Math.min(leftPx, this.rightPreview - widthPx);

      const topPx = Math.max(0, event.pageY - this.topPreview - this.shiftY);
      const heightPx = this.getStylePx('height');
      const finalTop = Math.min(topPx, this.bottomPreview - heightPx);

      this.updateElementPosition(finalLeft, finalTop);
      this.onClick();
    }
  }

  onMouseUp() {
    this.mouseDown = false;
  }

  async setGroupingStyles(element: FormatDetailModel, classEdit: any) {
    let border: string, borderBottom: string
    switch (element.border) {
      case 'noborder': {
        border = '0px none rgb(0, 0, 0)';
        borderBottom = '';
        break;
      }
      case 'border': {
        border = '1px solid rgb(0, 0, 0)';
        borderBottom = '';
        break;
      }
      case 'underline': {
        border = '0px none rgb(0, 0, 0)';
        borderBottom = '1px solid rgb(0, 0, 0)';
        break;
      }
    }

    classEdit.forEach((el: HTMLElement) => {
      el.style.fontSize = `${element.fontSize}px`;
      el.style.textAlign = element.alignment;
      el.style.fontWeight = element.bold;
      el.style.border = border;
      el.style.borderBottom = borderBottom;
      el.style.width = `${element.width}px`;
    });
  }

  private updateElementStyle(name: string, id: string | number) {
    this.element = document.getElementById(`${name}-${id}`);
    if (!this.element) return;
    this.element.style.fontSize = `${this.formatDetail.fontSize}px`;
  }

  private updateElementWidth(name: string, id: string | number) {
    this.element = document.getElementById(`${name}-${id}`);
    if (!this.element) return;
    this.element.style.width = `${this.formatDetail.width}%`;
  }

  private updateElementPosition(leftPx: number, topPx: number) {
    const leftPercentage = ((leftPx / this.rightPreview) * 100).toFixed(4);
    const topPercentage = ((topPx / this.bottomPreview) * 100).toFixed(4);

    this.element.style.left = `${leftPercentage}%`;
    this.element.style.top = `${topPercentage}%`;
  }

  private updateLabelText(formatId: number, text: string) {
    const labelElement = this.elementsLabel.find(x => x.formatDetailId === formatId);
    if (labelElement) {
      labelElement.text = text;
    }
  }

  async updateRangeFormatDetail() {
    this.elementsBodyActivities.forEach(element => {
      this.listFormatDetailUpdates.push(element);
    });

    this.elementsBodySupplies.forEach(element => {
      this.listFormatDetailUpdates.push(element);
    });

    const response = await firstValueFrom(this.formatDetailService.updateRangeFormatDetail(this.listFormatDetailUpdates));
    this.chargeFormat(response);
  }

}
